/**
 * Phase V — Global AI Control Settings page.
 * Configurable per-business-hours confidence threshold, default mode,
 * escalation rules, max consecutive AI replies, etc.
 *
 * Route: /admin/ai-settings
 */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Settings as SettingsIcon, Save, RefreshCw, Loader2, Shield,
  Bot, Clock, Activity, AlertTriangle, Power, ListChecks, ScrollText,
} from "lucide-react";

const MODES = ["off", "assist", "hybrid", "full_auto"];

// Must match backend/routes/ai_copilot.py::INTENT_TAGS exactly.
const INTENT_TAGS = [
  "greeting", "office_timing", "company_intro", "portfolio_request", "brochure_request",
  "package_info", "payment_confirmation", "meeting_confirmation", "file_acknowledgement", "thank_you",
  "price_inquiry", "wants_quotation", "wants_revision", "complaint", "support",
  "payment_followup", "interested", "site_visit", "wants_call", "construction_package",
  "3d_elevation", "interior", "floor_plan", "walkthrough", "boq",
  "discount_negotiation", "refund_request", "cancellation", "vip", "b2b_negotiation",
  "casual_query", "spam",
];
const POLICY_MODES = ["auto_eligible", "draft_only", "human_only"];
const POLICY_MODE_COLOR = {
  auto_eligible: "text-emerald-700 bg-emerald-50 border-emerald-200",
  draft_only: "text-amber-700 bg-amber-50 border-amber-200",
  human_only: "text-rose-700 bg-rose-50 border-rose-200",
};

const Field = ({ label, hint, children, testid }) => (
  <div className="space-y-1" data-testid={testid}>
    <label className="text-[11px] uppercase font-bold text-gray-600">{label}</label>
    {children}
    {hint && <p className="text-[10px] text-gray-500">{hint}</p>}
  </div>
);

export default function AISettings() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [policySaving, setPolicySaving] = useState(false);
  const [decisionLog, setDecisionLog] = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [resettingBreaker, setResettingBreaker] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, a, p] = await Promise.all([
        api.get("/admin/ai/global-settings"),
        api.get("/admin/ai/control-analytics?days=30"),
        api.get("/admin/ai/copilot/policies"),
      ]);
      setS(r.data);
      setAnalytics(a.data);
      setPolicy(p.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load AI settings");
    } finally { setLoading(false); }
  };

  const loadDecisionLog = async () => {
    setLogLoading(true);
    try {
      const r = await api.get("/admin/ai/decision-log?limit=50");
      setDecisionLog(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load decision log");
    } finally { setLogLoading(false); }
  };

  const loadHealth = async () => {
    setHealthLoading(true);
    try {
      const r = await api.get("/admin/ai/health");
      setHealth(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load AI health");
    } finally { setHealthLoading(false); }
  };

  const resetCircuitBreaker = async () => {
    setResettingBreaker(true);
    try {
      await api.put("/admin/ai/global-settings", { circuit_breaker_open: false });
      toast.success("Circuit breaker reset — AI resumes per normal policy");
      await Promise.all([loadHealth(), load()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset circuit breaker");
    } finally { setResettingBreaker(false); }
  };

  useEffect(() => { load(); loadDecisionLog(); loadHealth(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/ai/global-settings", {
        default_mode: s.default_mode,
        confidence_threshold: Number(s.confidence_threshold),
        after_hours_confidence_threshold: Number(s.after_hours_confidence_threshold),
        business_hours_start: Number(s.business_hours_start),
        business_hours_end: Number(s.business_hours_end),
        auto_pause_on_human_reply: !!s.auto_pause_on_human_reply,
        auto_resume: !!s.auto_resume,
        max_consecutive_ai_replies: Number(s.max_consecutive_ai_replies),
        max_consecutive_ai_replies_support: Number(s.max_consecutive_ai_replies_support),
        after_hours_force_draft: !!s.after_hours_force_draft,
        escalation_keywords: s.escalation_keywords,
        escalation_intents: s.escalation_intents,
        escalation_flags: s.escalation_flags,
        sales_auto_enabled: !!s.sales_auto_enabled,
        support_auto_enabled: !!s.support_auto_enabled,
        daily_token_limit: Number(s.daily_token_limit) || 0,
        monthly_token_limit: Number(s.monthly_token_limit) || 0,
      });
      setS(r.data);
      toast.success("AI settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const setIntentMode = (intent, channel, mode) => {
    const current = (policy?.intent_modes || {})[intent] || {};
    const nextModes = { ...(policy?.intent_modes || {}), [intent]: { ...current, [channel]: mode } };
    setPolicy((prev) => ({ ...prev, intent_modes: nextModes }));
  };

  // The kill-switches live in two different backend documents (ai_policies for
  // the global switch + intent matrix, ai_control_settings for everything
  // else) but read as one panel — one button saves both.
  const saveAutonomy = async () => {
    setSaving(true);
    setPolicySaving(true);
    try {
      await Promise.all([
        api.put("/admin/ai/global-settings", {
          max_consecutive_ai_replies_support: Number(s.max_consecutive_ai_replies_support),
          sales_auto_enabled: !!s.sales_auto_enabled,
          support_auto_enabled: !!s.support_auto_enabled,
        }).then((r) => setS((prev) => ({ ...prev, ...r.data }))),
        api.put("/admin/ai/copilot/policies", {
          auto_send_enabled_globally: !!policy.auto_send_enabled_globally,
          min_confidence_for_auto: Number(policy.min_confidence_for_auto),
          intent_modes: policy.intent_modes,
        }).then((r) => setPolicy(r.data)),
      ]);
      toast.success("AI autonomy settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed — intent matrix / global switch need super_admin");
    } finally {
      setSaving(false);
      setPolicySaving(false);
    }
  };

  if (loading || !s || !policy) {
    return (
      <div className="p-10 text-center text-gray-500" data-testid="ai-settings-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-purple-600" />
        Loading AI control settings…
      </div>
    );
  }

  const change = (k, v) => setS(prev => ({ ...prev, [k]: v }));
  const changeList = (k, csv) => setS(prev => ({ ...prev, [k]: csv.split(",").map(x => x.trim()).filter(Boolean) }));

  return (
    <div className="space-y-5" data-testid="admin-ai-settings">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#1a3c5e] flex items-center gap-2">
            <SettingsIcon className="w-6 h-6 text-purple-600" /> AI Control Settings
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Govern when AI may auto-reply, when it must wait for a human, and escalation rules.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={saving} data-testid="ai-settings-reload" className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
            <RefreshCw className="w-3.5 h-3.5" /> Reload
          </button>
          <button onClick={save} disabled={saving} data-testid="ai-settings-save" className="px-3 py-1.5 bg-[#1a3c5e] hover:bg-[#0f2940] text-white rounded-lg text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save settings
          </button>
        </div>
      </div>

      {/* Autonomy Kill-Switches — the unified panel (A3): global switch + per-channel toggles
          used to live scattered/missing entirely; this is the one place to control them. */}
      <div className="bg-white border-2 border-purple-200 rounded-2xl p-4 space-y-3" data-testid="ai-autonomy-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2"><Power className="w-4 h-4 text-purple-600" /> AI Autonomy — kill switches</h3>
          <button onClick={saveAutonomy} disabled={saving || policySaving} data-testid="save-autonomy"
            className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
            {(saving || policySaving) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save autonomy settings
          </button>
        </div>
        <p className="text-[11px] text-gray-500">
          The master switch below gates every conversation, everywhere — turning it off caps every AI reply at draft-only regardless of mode.
          The two channel switches are independent: support can be off while sales stays full-auto, or vice versa.
        </p>
        <label className={`flex items-center gap-3 p-3 rounded-xl border ${policy.auto_send_enabled_globally ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"}`} data-testid="set-global-kill-switch">
          <input type="checkbox" checked={!!policy.auto_send_enabled_globally}
            onChange={(e) => setPolicy(prev => ({ ...prev, auto_send_enabled_globally: e.target.checked }))} />
          <span className="text-sm">
            <strong>Global auto-send</strong> — {policy.auto_send_enabled_globally ? "ON: AI may send real replies when every other gate passes" : "OFF: every AI reply is capped at draft-only, no matter what"}
          </span>
        </label>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className={`flex items-center gap-3 p-3 rounded-xl border ${s.sales_auto_enabled ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"}`} data-testid="set-sales-auto">
            <input type="checkbox" checked={!!s.sales_auto_enabled} onChange={(e) => change("sales_auto_enabled", e.target.checked)} />
            <span className="text-sm"><strong>Sales conversations</strong> — auto-send channel switch</span>
          </label>
          <label className={`flex items-center gap-3 p-3 rounded-xl border ${s.support_auto_enabled ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"}`} data-testid="set-support-auto">
            <input type="checkbox" checked={!!s.support_auto_enabled} onChange={(e) => change("support_auto_enabled", e.target.checked)} />
            <span className="text-sm"><strong>Support conversations</strong> — auto-send channel switch</span>
          </label>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Min. confidence to auto-send (global policy)" hint="0.0 - 1.0 · applies on top of the per-conversation threshold" testid="set-min-conf-policy">
            <input type="number" step="0.01" min="0" max="1" value={policy.min_confidence_for_auto}
              onChange={(e) => setPolicy(prev => ({ ...prev, min_confidence_for_auto: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
          </Field>
          <Field label="Max consecutive AI replies — support" hint="Stricter than sales · default 2 · 0 = no cap (free-flowing)" testid="set-max-consec-support">
            <input type="number" min="0" max="20" value={s.max_consecutive_ai_replies_support}
              onChange={(e) => change("max_consecutive_ai_replies_support", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
          </Field>
        </div>
      </div>

      {/* Intent Policy Matrix — A5: split by engagement_type, the same intent can
          carry a different ceiling in a sales conversation vs a support one. */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3" data-testid="ai-intent-matrix">
        <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2"><ListChecks className="w-4 h-4 text-purple-600" /> Intent policy matrix</h3>
        <p className="text-[11px] text-gray-500">Per-intent, per-channel ceiling on top of the kill switches above — <span className="font-semibold text-emerald-700">auto-eligible</span> can still send, <span className="font-semibold text-amber-700">draft-only</span> always waits for approval, <span className="font-semibold text-rose-700">human-only</span> always escalates. A price inquiry, for example, can auto-send in Sales while drafting + flagging cross-sell in Support.</p>
        <div className="overflow-x-auto max-h-96 overflow-y-auto pr-1">
          <table className="w-full text-xs" data-testid="intent-matrix-table">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-[10px] uppercase text-gray-500 border-b border-gray-200">
                <th className="py-1.5 pr-3">Intent</th>
                <th className="py-1.5 pr-3">Sales</th>
                <th className="py-1.5 pr-3">Support</th>
              </tr>
            </thead>
            <tbody>
              {INTENT_TAGS.map((intent) => {
                const entry = (policy.intent_modes || {})[intent] || {};
                return (
                  <tr key={intent} className="border-b border-gray-100" data-testid={`intent-row-${intent}`}>
                    <td className="py-1.5 pr-3 font-semibold">{intent}</td>
                    {["sales", "support"].map((channel) => {
                      const mode = entry[channel] || "draft_only";
                      return (
                        <td key={channel} className="py-1.5 pr-3">
                          <select value={mode} onChange={(e) => setIntentMode(intent, channel, e.target.value)}
                            className={`text-[11px] font-bold rounded px-1.5 py-1 border ${POLICY_MODE_COLOR[mode]}`}
                            data-testid={`intent-${intent}-${channel}`}>
                            {POLICY_MODES.map(m => <option key={m} value={m}>{m.replace("_", " ")}</option>)}
                          </select>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Behaviour */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3" data-testid="ai-settings-behaviour">
        <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2"><Bot className="w-4 h-4 text-purple-600" /> Default behaviour</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Default AI mode (new conversations)" testid="set-default-mode">
            <select value={s.default_mode} onChange={(e) => change("default_mode", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white">
              {MODES.map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
            </select>
          </Field>
          <Field label="Confidence threshold (business hours)" hint="0.0 - 1.0 · default 0.90" testid="set-thr-day">
            <input type="number" step="0.01" min="0" max="1" value={s.confidence_threshold}
              onChange={(e) => change("confidence_threshold", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
          </Field>
          <Field label="Confidence threshold (after hours)" hint="Stricter for nights · default 0.95" testid="set-thr-night">
            <input type="number" step="0.01" min="0" max="1" value={s.after_hours_confidence_threshold}
              onChange={(e) => change("after_hours_confidence_threshold", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
          </Field>
          <Field label="Business hours start" hint="0-23 · IST hour" testid="set-bh-start">
            <input type="number" min="0" max="23" value={s.business_hours_start}
              onChange={(e) => change("business_hours_start", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
          </Field>
          <Field label="Business hours end" hint="1-24 · IST hour" testid="set-bh-end">
            <input type="number" min="1" max="24" value={s.business_hours_end}
              onChange={(e) => change("business_hours_end", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
          </Field>
          <Field label="Max consecutive AI replies" hint="Stop auto-sending after N in a row · 0 = no cap (free-flowing)" testid="set-max-consec">
            <input type="number" min="0" max="20" value={s.max_consecutive_ai_replies}
              onChange={(e) => change("max_consecutive_ai_replies", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
          </Field>
          <Field label="Daily token budget" hint="0 = no limit · blocks new AI calls once hit (A9)" testid="set-daily-token-limit">
            <input type="number" min="0" value={s.daily_token_limit || 0}
              onChange={(e) => change("daily_token_limit", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
          </Field>
          <Field label="Monthly token budget" hint="0 = no limit · blocks new AI calls once hit (A9)" testid="set-monthly-token-limit">
            <input type="number" min="0" value={s.monthly_token_limit || 0}
              onChange={(e) => change("monthly_token_limit", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 pt-2">
          <label className="flex items-center gap-2 text-sm" data-testid="set-auto-pause">
            <input type="checkbox" checked={!!s.auto_pause_on_human_reply}
              onChange={(e) => change("auto_pause_on_human_reply", e.target.checked)} />
            <span><strong>Auto-pause</strong> when a human sends a message</span>
          </label>
          <label className="flex items-center gap-2 text-sm" data-testid="set-auto-resume">
            <input type="checkbox" checked={!!s.auto_resume}
              onChange={(e) => change("auto_resume", e.target.checked)} />
            <span><strong>Auto-resume</strong> after pause timer expires</span>
          </label>
          <label className="flex items-center gap-2 text-sm" data-testid="set-after-hours-draft">
            <input type="checkbox" checked={!!s.after_hours_force_draft}
              onChange={(e) => change("after_hours_force_draft", e.target.checked)} />
            <span>Outside business hours → <strong>force draft only</strong> (never auto-send)</span>
          </label>
        </div>
      </div>

      {/* Escalation */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3" data-testid="ai-settings-escalation">
        <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2"><Shield className="w-4 h-4 text-rose-600" /> Escalation rules</h3>
        <p className="text-[11px] text-gray-500">AI will never auto-reply for these intents/flags — drafts only, human must approve.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Escalation intents" hint="Comma-separated · e.g. refund_request, complaint" testid="set-esc-intents">
            <textarea value={(s.escalation_intents || []).join(", ")} rows={2}
              onChange={(e) => changeList("escalation_intents", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white font-mono" />
          </Field>
          <Field label="Escalation conversation flags" hint="Field names on the conversation doc" testid="set-esc-flags">
            <textarea value={(s.escalation_flags || []).join(", ")} rows={2}
              onChange={(e) => changeList("escalation_flags", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white font-mono" />
          </Field>
          <Field label="Escalation keywords (info only)" hint="Used in audit/analytics reports" testid="set-esc-keywords">
            <textarea value={(s.escalation_keywords || []).join(", ")} rows={2}
              onChange={(e) => changeList("escalation_keywords", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white font-mono" />
          </Field>
        </div>
      </div>

      {/* Analytics */}
      {analytics && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3" data-testid="ai-settings-analytics">
          <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2"><Activity className="w-4 h-4 text-emerald-600" /> Last 30 days — AI Control activity</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            <Block title="Mode distribution" Icon={Bot} rows={analytics.modes_distribution} testid="analytics-modes" />
            <Block title="Event log" Icon={Clock} rows={analytics.events} testid="analytics-events" />
            <Block title="Auto-skip reasons" Icon={AlertTriangle} rows={analytics.skip_reasons} highlight testid="analytics-skip-reasons" />
          </div>
        </div>
      )}

      {/* AI Health — A8 observability: Gemini error rate, circuit breaker state, cost */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3" data-testid="ai-health-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2"><Activity className="w-4 h-4 text-blue-600" /> AI health</h3>
          <button onClick={loadHealth} disabled={healthLoading} data-testid="reload-ai-health"
            className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
            {healthLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
          </button>
        </div>
        {health && (
          <>
            <div className={`rounded-xl border p-3 flex items-center justify-between flex-wrap gap-2 ${
              health.circuit_breaker.open ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"
            }`} data-testid="circuit-breaker-status">
              <div className="flex items-center gap-2">
                <Power className={`w-4 h-4 ${health.circuit_breaker.open ? "text-rose-600" : "text-emerald-600"}`} />
                <div>
                  <div className={`text-xs font-bold ${health.circuit_breaker.open ? "text-rose-700" : "text-emerald-700"}`}>
                    Circuit breaker: {health.circuit_breaker.open ? "OPEN — forcing human review" : "Closed — normal policy applies"}
                  </div>
                  {health.circuit_breaker.open && (
                    <div className="text-[11px] text-rose-600 mt-0.5">
                      {health.circuit_breaker.reason} · opened {health.circuit_breaker.opened_at ? new Date(health.circuit_breaker.opened_at).toLocaleString() : "—"}
                    </div>
                  )}
                </div>
              </div>
              {health.circuit_breaker.open && (
                <button onClick={resetCircuitBreaker} disabled={resettingBreaker} data-testid="reset-circuit-breaker"
                  className="px-3 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-bold disabled:opacity-60">
                  {resettingBreaker ? "Resetting…" : "Reset now"}
                </button>
              )}
            </div>
            <div className="grid sm:grid-cols-4 gap-3 text-xs">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3" data-testid="health-error-rate-1h">
                <div className="text-[10px] uppercase font-bold text-gray-600 mb-1">Gemini error rate (1h)</div>
                <div className="text-lg font-bold text-[#1a3c5e] tabular-nums">{Math.round(health.gemini_error_rate_1h.error_rate * 100)}%</div>
                <div className="text-gray-500">{health.gemini_error_rate_1h.failed}/{health.gemini_error_rate_1h.total} calls failed</div>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3" data-testid="health-error-rate-24h">
                <div className="text-[10px] uppercase font-bold text-gray-600 mb-1">Gemini error rate (24h)</div>
                <div className="text-lg font-bold text-[#1a3c5e] tabular-nums">{Math.round(health.gemini_error_rate_24h.error_rate * 100)}%</div>
                <div className="text-gray-500">{health.gemini_error_rate_24h.failed}/{health.gemini_error_rate_24h.total} calls failed</div>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3" data-testid="health-fact-gate-rejections">
                <div className="text-[10px] uppercase font-bold text-gray-600 mb-1">Fact-gate rejections (24h)</div>
                <div className="text-lg font-bold text-[#1a3c5e] tabular-nums">{health.fact_gate_rejections_24h}</div>
                <div className="text-gray-500">hallucinated price/timeline blocked (A4)</div>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3" data-testid="health-sla-breaches">
                <div className="text-[10px] uppercase font-bold text-gray-600 mb-1">Open SLA breaches</div>
                <div className="text-lg font-bold text-[#1a3c5e] tabular-nums">{health.sla_breaches_open}</div>
                <div className="text-gray-500">across sales + support (A6)</div>
              </div>
            </div>
            <div className="text-[11px] text-gray-500">
              Est. AI spend — today: <span className="font-bold text-gray-700">${health.est_cost_usd_today.toFixed(4)}</span> · this month: <span className="font-bold text-gray-700">${health.est_cost_usd_month.toFixed(2)}</span>
            </div>
            {/* A9: token budget — only shown once an admin has actually set a cap (0 = no limit) */}
            {(health.token_budget.daily_pct != null || health.token_budget.monthly_pct != null) && (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="token-budget-usage">
                {health.token_budget.daily_pct != null && (
                  <BudgetBar label="Daily token budget" used={health.token_budget.daily_used} limit={health.token_budget.daily_limit} pct={health.token_budget.daily_pct} testid="budget-daily" />
                )}
                {health.token_budget.monthly_pct != null && (
                  <BudgetBar label="Monthly token budget" used={health.token_budget.monthly_used} limit={health.token_budget.monthly_limit} pct={health.token_budget.monthly_pct} testid="budget-monthly" />
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Decision Log — the audit trace every gating decision now writes (A3) */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3" data-testid="ai-decision-log">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2"><ScrollText className="w-4 h-4 text-purple-600" /> Decision log — live feed</h3>
          <button onClick={loadDecisionLog} disabled={logLoading} data-testid="reload-decision-log"
            className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
            {logLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
          </button>
        </div>
        {decisionLog && (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <Block title="By outcome" Icon={Bot} rows={decisionLog.by_decision} testid="decision-log-by-outcome" />
              <Block title="By rule fired" Icon={Shield} rows={decisionLog.by_rule} testid="decision-log-by-rule" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="decision-log-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase text-gray-500 border-b border-gray-200">
                    <th className="py-1.5 pr-3">When</th>
                    <th className="py-1.5 pr-3">Channel</th>
                    <th className="py-1.5 pr-3">Intent</th>
                    <th className="py-1.5 pr-3">Confidence</th>
                    <th className="py-1.5 pr-3">Outcome</th>
                    <th className="py-1.5 pr-3">Rule</th>
                    <th className="py-1.5 pr-3">ms</th>
                  </tr>
                </thead>
                <tbody>
                  {(decisionLog.rows || []).length === 0 ? (
                    <tr><td colSpan={7} className="py-3 text-gray-400 italic">No decisions logged yet</td></tr>
                  ) : decisionLog.rows.map((r) => (
                    <tr key={r.id} className="border-b border-gray-100">
                      <td className="py-1.5 pr-3 text-gray-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="py-1.5 pr-3">{r.engagement_type}</td>
                      <td className="py-1.5 pr-3 font-mono">{r.intent || "—"}</td>
                      <td className="py-1.5 pr-3 tabular-nums">{r.confidence != null ? Number(r.confidence).toFixed(2) : "—"}</td>
                      <td className="py-1.5 pr-3">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${r.final_decision === "auto" ? "bg-emerald-100 text-emerald-700" : r.final_decision === "human" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                          {r.final_decision}
                        </span>
                      </td>
                      <td className="py-1.5 pr-3 font-mono text-gray-600">{r.final_rule}</td>
                      <td className="py-1.5 pr-3 tabular-nums text-gray-500">{r.latency_ms}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Block({ title, Icon, rows, highlight, testid }) {
  const entries = Object.entries(rows || {}).sort((a, b) => b[1] - a[1]).slice(0, 12);
  return (
    <div className={`rounded-xl border p-3 ${highlight ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200"}`} data-testid={testid}>
      <div className="text-[10px] uppercase font-bold text-gray-700 flex items-center gap-1 mb-1.5"><Icon className="w-3 h-3" /> {title}</div>
      {entries.length === 0 ? (
        <div className="text-xs text-gray-500 italic">No activity yet</div>
      ) : (
        <ul className="space-y-1">
          {entries.map(([k, v]) => (
            <li key={k} className="flex items-center justify-between text-xs">
              <span className="text-gray-700 font-semibold truncate mr-2">{k}</span>
              <span className="text-[#1a3c5e] font-bold tabular-nums">{v}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BudgetBar({ label, used, limit, pct, testid }) {
  const clamped = Math.min(1, pct);
  const over = pct >= 1;
  const nearLimit = pct >= 0.8;
  const barColor = over ? "bg-rose-600" : nearLimit ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs" data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase font-bold text-gray-600">{label}</span>
        <span className="font-bold tabular-nums text-gray-700">{Math.round(pct * 100)}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.round(clamped * 100)}%` }} />
      </div>
      <div className="mt-1 text-gray-500 tabular-nums">{used.toLocaleString()} / {limit.toLocaleString()} tokens</div>
    </div>
  );
}
